/**
 * Stato MOSTRATO dal sito (decisione del committente, «opzione 1»): lo stato
 * salvato resta `chiuso`, ma il sito distingue i chiusi «speciali».
 *
 * - «Unito»: ticket unito in un altro (`mergedInto` valorizzato, o `merged: true`
 *   nella vista del richiedente, che non riceve l'id). Ha la precedenza.
 * - «Indesiderato»: ticket chiuso col mittente ignorato (tag `spam`). Solo agli
 *   operatori: il richiedente vede «Chiuso».
 * - Tutti gli altri: l'etichetta di sempre (operatore o richiedente).
 *
 * Funzione pura: nessun enum nuovo, nessuna transizione toccata. L'app mobile
 * resta su «Chiuso».
 */
import { STATUS_LABELS } from './constants';

export type DerivedStatus = 'merged' | 'spam';

export interface DisplayStatusInput {
  status: string;
  mergedInto?: string | null;
  /** Vista del richiedente: solo il fatto, senza id. */
  merged?: boolean;
  /** Numero del ticket superstite, se la riga lo porta (coda operatore). */
  mergedIntoNumber?: string | null;
  tags?: string[] | null;
}

export interface DisplayStatus {
  /** Stato salvato, invariato. */
  status: string;
  label: string;
  derived: DerivedStatus | null;
  /** Tooltip della variante derivata. */
  title?: string;
}

export const SPAM_TAG = 'spam';

export const displayStatus = (
  t: DisplayStatusInput,
  opts: { viewer?: 'operator' | 'requester'; labels?: Record<string, string> } = {}
): DisplayStatus => {
  const { viewer = 'operator', labels = STATUS_LABELS } = opts;
  const base: DisplayStatus = { status: t.status, label: labels[t.status] || t.status, derived: null };
  if (t.mergedInto || t.merged === true) {
    return {
      ...base,
      label: 'Unito',
      derived: 'merged',
      title: t.mergedIntoNumber ? `Unito nel ticket #${t.mergedIntoNumber}` : 'Unito in un altro ticket',
    };
  }
  const spam = (t.tags || []).some((x) => String(x).toLowerCase() === SPAM_TAG);
  if (viewer === 'operator' && t.status === 'chiuso' && spam) {
    return { ...base, label: 'Indesiderato', derived: 'spam', title: 'Mittente ignorato' };
  }
  return base;
};

/**
 * Etichette per `StatusBadge` nella vista del richiedente: quelle di sempre, con
 * «Unito» al posto di «Chiuso» per un ticket unito (mai «Indesiderato»). Le
 * etichette al femminile (ospite: «la richiesta», «Chiusa») passano
 * `GUEST_MERGED`, cosi la concordanza resta quella della pagina.
 */
export const GUEST_MERGED = { label: 'Unita', title: 'Unita a un’altra richiesta' };

export const requesterBadgeLabels = (
  t: DisplayStatusInput,
  labels: Record<string, string>,
  merged?: { label: string; title: string }
): { labels: Record<string, string>; title?: string } => {
  const d = displayStatus(t, { viewer: 'requester', labels });
  if (!d.derived) return { labels };
  return {
    labels: { ...labels, [t.status]: merged ? merged.label : d.label },
    title: merged ? merged.title : d.title,
  };
};
